// src/components/JobTailorInput.tsx
import React, { useState } from "react";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";
import "../styles/JobTailorInput.css";

interface JobTailorInputProps {
  resumeId?: string;
  onTailoringComplete: (tailoringData: any) => void;
  onClose: () => void;
}

const JobTailorInput: React.FC<JobTailorInputProps> = ({
  resumeId,
  onTailoringComplete,
  onClose,
}) => {
  const [jobDescription, setJobDescription] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"paste" | "manual">("paste");

  const handleTailor = async () => {
    if (!jobDescription.trim()) {
      toast.error("Please provide a job description");
      return;
    }

    if (jobDescription.trim().length < 50) {
      toast.error("Job description seems too short. Please provide more details.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosInstance.post("/auth/resume/tailor-for-job", {
        resumeId,
        jobDescription,
        jobTitle: jobTitle || "Position",
        companyName: companyName || "Company",
      });

      toast.success("Resume tailoring analysis complete!");
      onTailoringComplete(response.data);
    } catch (err: any) {
      console.error("Tailoring error:", err);
      toast.error(err.response?.data?.error || "Failed to tailor resume");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="jti-overlay">
      <div className="jti-modal">
        <div className="jti-header">
          <h2>Tailor Resume for Job</h2>
          <button className="jti-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="jti-body">
          {/* Job Info Fields */}
          <div className="jti-job-info">
            <div className="jti-field">
              <label>Job Title (Optional)</label>
              <input
                type="text"
                placeholder="e.g., Senior Software Engineer"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className="jti-input"
              />
            </div>

            <div className="jti-field">
              <label>Company Name (Optional)</label>
              <input
                type="text"
                placeholder="e.g., Google"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="jti-input"
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="jti-tabs">
            <button
              className={`jti-tab ${activeTab === "paste" ? "active" : ""}`}
              onClick={() => setActiveTab("paste")}
            >
              📋 Paste Job Description
            </button>
            <button
              className={`jti-tab ${activeTab === "manual" ? "active" : ""}`}
              onClick={() => setActiveTab("manual")}
            >
              ✍️ Describe Requirements
            </button>
          </div>

          {/* Job Description Input */}
          <div className="jti-field">
            <label>
              {activeTab === "paste"
                ? "Paste the complete job posting here"
                : "Describe the key requirements and skills needed"}
            </label>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder={
                activeTab === "paste"
                  ? "Paste the job posting text here (from LinkedIn, company website, job board, etc.)..."
                  : "E.g., 5+ years React, Node.js, TypeScript, AWS, team lead experience..."
              }
              className="jti-textarea"
              rows={8}
            />
            <div className="jti-char-count">
              {jobDescription.length} characters
            </div>
          </div>

          {/* Help Text */}
          <div className="jti-help">
            <p>
              💡 <strong>Tip:</strong> The more detailed the job description, the
              better the tailoring recommendations. Include responsibilities,
              required skills, experience level, and qualifications.
            </p>
          </div>
        </div>

        <div className="jti-footer">
          <button className="jti-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="jti-btn-analyze"
            onClick={handleTailor}
            disabled={isLoading || jobDescription.trim().length < 50}
          >
            {isLoading ? "🔄 Analyzing..." : "🎯 Analyze & Get Recommendations"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobTailorInput;
